import { StateCreator } from 'zustand'

import {
    IResponse,
    IRocketTokens,
    ProfileDto,
    ProfileShareSection,
    spErrorTags,
    UpdateUserDTO,
    UserOrganisation,
} from '@eventr/common'
import * as Sentry from '@sentry/capacitor'
import { EventrGatewayAPI } from '../../data/gateway.api'
import { BoundStore, bs, useBoundStore } from '../../state/bound.store'
import { lls } from '../../state/liveLocationState/liveLocation.store'
import { loadingSignal } from '../../state/overlayState/overlaySignals'
import {
    filterAndExtractIds,
    findMissingData,
} from '../account/profilePage/userProfileUtils'
import { removeAllOrgDataFromLocalStorage } from '../account/utils'
import { useGuestStore } from '../guest/guest.store'
import { checkForTermsAndConditions } from './checkTermsAndCond'
interface ProfileState {
    profile?: ProfileDto
    rcTokens: IRocketTokens[]
    selectedOrg: UserOrganisation | undefined
    additionalInfo?: ProfileShareSection[]
    profileRequired?: string[]
}

interface ProfileActions {
    fetchProfile: () => Promise<IResponse<ProfileDto>>
    getProfile: () => Promise<ProfileDto | undefined>
    getRcTokens: () => void
    checkUserOwnership: () => boolean
    deleteRequest: () => Promise<void>
    setSelectedOrg: (org: UserOrganisation) => Promise<void>
    updateRequest: (body: UpdateUserDTO) => Promise<ProfileDto>
    passwordRecoveryRequest: (email: string) => Promise<IResponse<any>>
    setProfileShareSections: () => Promise<ProfileShareSection[]>
    setSelectedOrgById: (orgId: string) => Promise<void>
    getProfileShareSections: () => ProfileShareSection[]
    getProfileSharedInfo: () => ProfileShareSection[]
}

export interface ProfileSlice extends ProfileState, ProfileActions { }

export const createProfleSlice: StateCreator<
    BoundStore,
    [['zustand/devtools', never]],
    [],
    ProfileSlice
> = (set, get) => ({
    profile: undefined,
    rcTokens: [],
    selectedOrg: undefined,

    fetchProfile: async () => {
        return EventrGatewayAPI.profile.getMe()
    },
    getProfile: async () => {
        try {
            const profile = await EventrGatewayAPI.profile.getMe()
            if (profile.status === 'success' && profile.data) {
                set({ profile: profile.data })
                return profile.data
            }
            return get().profile
        } catch (error) {
            console.error(error)
            return get().profile
        }
    },
    getRcTokens: async () => {
        const rcTokens = await EventrGatewayAPI.rocketchat.getRocketchatToken()

        if (rcTokens.status === 'success') {
            set({ rcTokens: rcTokens.data })
        } else {
            set({ rcTokens: [] })
        }
    },
    checkUserOwnership: () => {
        const { profile } = get()
        if (!profile) return false
        const isOwner = profile.organisations.some((org) => {
            return (
                org.roles &&
                org.roles.some((role) => {
                    return role === 'owner'
                })
            )
        })
        return isOwner
    },
    deleteRequest: async () => {
        const resp = await EventrGatewayAPI.profile.delete()
        if (resp.status === 'success') {
            bs().logout()
        } else {
            spErrorTags(resp.message, ['PROFILE'], resp)
        }
    },
    setSelectedOrg: async (org) => {
        console.log('setSelectedOrg', org)
        if (org) {
            if (useGuestStore.getState().isGuestMode) {
                set({
                    selectedOrg: { name: '__guest__' },
                    orgConfig: {},
                    wpUser: undefined,
                })
                return
            }
            removeAllOrgDataFromLocalStorage()
            set({ selectedOrg: org })
            // dont make requests if org is being created
            if (org.status === 'creating') return
            set({ itineraryData: {} })
            await get().fetchUsers()
            // make all org request
            const promises = []
            promises.push(
                get().fetchOrgConfig(),
                get().fetchFields(),
                get().fetchSections(),
                get().forceItineraryMapUpdate(),
                get().loadRequests(),
                get().fetchAllLists(),
                lls().stopSharingLiveLocation(),
                lls().init()
            )
            await Promise.all(promises)
            // resolve itinerary to be selected
            await get().resolveItinId()
        } else {
            set({ selectedOrg: org, wpUser: undefined, orgConfig: {} })
        }
    },
    setSelectedOrgById: async (orgId: string) => {
        loadingSignal.value = true
        const org = get().profile?.organisations?.find(
            (org) => org.name === orgId
        )
        if (org) {
            await get().setSelectedOrg(org)
        } else {
            const newProfile = await get().getProfile()
            const newOrg = newProfile?.organisations?.find(
                (org) => org.name === orgId
            )
            if (newOrg) await get().setSelectedOrg(newOrg)
            spErrorTags('Org not found in user profile ' + orgId)
        }
        loadingSignal.value = false
    },
    updateRequest: async (body) => {
        const resp = await EventrGatewayAPI.profile.update(body)
        if (resp.status === 'success' && resp.data) {
            set({
                profile: {
                    ...resp.data,
                },
                selectedOrg: resp.data.organisations?.find(
                    (s) => s.name === bs().selectedOrg?.name
                ),
            })
            return resp.data
        } else {
            spErrorTags(resp.message, ['PROFILE'], resp)
            throw new Error('Failed to update profile')
        }
    },
    passwordRecoveryRequest: async (email: string) => {
        const resp = await EventrGatewayAPI.profile.forgotPassword(
            email.toLowerCase()
        )
        return resp
    },
    setProfileShareSections: async (): Promise<ProfileShareSection[]> => {
        try {
            const resp =
                await EventrGatewayAPI.profile.profileShare.fetchAdditionalInfoFields()
            if (resp.status === 'success') {
                const sections = filterAndExtractIds(
                    bs()
                        .getProfileShareSections()
                        .filter((s) => s.required && s.enabled)
                )
                set({
                    profileRequired:
                        findMissingData(
                            sections,
                            bs().selectedOrg?.sharedProperties ?? []
                        ) ?? [],
                })
                set({
                    additionalInfo: resp.data ?? [],
                })
                return resp.data
            } else {
                throw new Error('Could not fetch additonal info data')
            }
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED') return []
            spErrorTags(
                'Could not fetch additonal info data',
                ['PROFILE'],
                error
            )
            return []
        }
    },
    getProfileShareSections: (): ProfileShareSection[] => {
        const sections = (get()?.additionalInfo as ProfileShareSection[]) ?? []
        return sections
    },
    getProfileSharedInfo: (): ProfileShareSection[] => {
        return (get().profile?.additionalInfo as ProfileShareSection[]) ?? []
    },
})

// subscriptions to the store

setTimeout(() => {
    useBoundStore.subscribe(
        (state) => state.profile,
        (profile, prev) => {
            if (profile) {
                //set user context for sentry
                Sentry.setUser({ email: profile?.email, id: profile?._id })
                if (bs().loginCompleted) {
                    //deal with when user is removed from org

                    const selectedOrg = bs().selectedOrg
                    const org = profile.organisations?.find(
                        (org) => org.name === selectedOrg?.name
                    )
                    if (org === undefined && selectedOrg !== undefined) {
                        console.log(
                            'selected org not found, setting to first org'
                        )
                        bs().setSelectedOrg(getReadyOrgs(profile)[0])
                    }

                    if (prev && !bs().selectedOrg) {
                        console.log(
                            'bs().selectedOrg is undefined, setting to first org'
                        )

                        bs().setSelectedOrg(getReadyOrgs(profile)[0])
                    }
                }

                checkForTermsAndConditions(profile)
                // if no ready orgs, wait 10 seconds and try again
                if (getReadyOrgs(profile).length === 0) {
                    setTimeout(async () => {
                        await bs().getProfile()
                    }, 5000)
                }
                // rerun login request if selected orgs states changed from creating to active
                selectedOrgStatusChanged(profile, prev)
            }
        },
        {
            fireImmediately: true,
        }
    )
}, 100)

//Use this to get the orgs that are not in the creating state as we dont want to show them
export const getReadyOrgs = (profile: ProfileDto) => {
    return (profile?.organisations ?? []).filter((org) => org.status !== 'creating') ?? []
}

/**
 * If the selected org status has changed from creating to active, rerun the
 * onLogin function to set the correct org context
 * @param currentProfile the current profile
 * @param previousProfile the previous profile
 */
const selectedOrgStatusChanged = async (
    currentProfile?: ProfileDto,
    previousProfile?: ProfileDto
) => {
    if (!currentProfile || !previousProfile) return
    const currentOrg = bs().selectedOrg
    const currentProfileOrg = currentProfile?.organisations?.find(
        (org) => org.name === currentOrg?.name
    )
    const previousProfileOrg = previousProfile?.organisations?.find(
        (org) => org.name === currentOrg?.name
    )

    if (
        currentProfileOrg?.status === 'active' &&
        previousProfileOrg?.status === 'creating'
    ) {
        bs().onLogin()
    }
}
